import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Flame, MessageCircle, X, TrendingUp } from "lucide-react";


const MOOD_EMOJI = {
  elated: "🤩", happy: "😄", focused: "🧐",
  concerned: "😟", worried: "😰", sleepy: "😴",
};


/** Compact companion card mounted on Dashboard. */
export function CompanionWidget() {
  const [status, setStatus] = useState(null);
  const [showConsult, setShowConsult] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/companion/status"); setStatus(data); }
      catch { /* silent */ }
    })();
  }, []);

  if (!status) return null;
  const goalsDone = (status.weekly_goals || []).filter(g => g.done).length;
  const goalsTotal = (status.weekly_goals || []).length;

  return (
    <section className="card-sharp p-5 mb-6 relative overflow-hidden" data-testid="companion-widget">
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none bg-[radial-gradient(circle_at_15%_15%,_var(--accent),_transparent_60%)]"/>
      <div className="relative flex items-start gap-4 flex-wrap">
        <div className="text-5xl leading-none select-none" data-testid="widget-mood">
          {MOOD_EMOJI[status.mood] || "🤖"}
        </div>
        <div className="flex-1 min-w-[240px]">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={12} className="text-[var(--accent)]"/>
            <span className="chip">FORGE · YOUR COMPANION</span>
          </div>
          <h3 className="font-display text-lg text-white" data-testid="widget-greeting">{status.greeting}</h3>
          <div className="flex flex-wrap gap-4 mt-2 text-xs font-mono">
            <span className="flex items-center gap-1 text-[var(--accent)]" data-testid="widget-streak">
              <Flame size={12}/> {status.streak.streak}-day streak
            </span>
            <span className="text-[var(--text-secondary)]" data-testid="widget-xp">
              XP {status.streak.xp} · Lv {status.streak.level}
            </span>
            <span className="text-[var(--text-secondary)]" data-testid="widget-goals">
              Weekly goals: {goalsDone}/{goalsTotal}
            </span>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <button data-testid="widget-consult-btn" onClick={()=>setShowConsult(true)}
            className="btn-primary flex items-center gap-2 text-xs">
            <TrendingUp size={13}/> Ask about a trade
          </button>
          <Link to="/companion" data-testid="widget-chat-link"
            className="btn-outline flex items-center gap-2 text-xs">
            <MessageCircle size={13}/> Open chat
          </Link>
        </div>
      </div>
      {showConsult && <TradeConsultModal onClose={()=>setShowConsult(false)}/>}
    </section>
  );
}


export function TradeConsultModal({ onClose, prefill }) {
  const [form, setForm] = useState({
    symbol: prefill?.symbol || "EURUSD",
    side: prefill?.side || "buy",
    entry_price: prefill?.entry_price || "",
    sl_price: prefill?.sl_price || "",
    tp_price: prefill?.tp_price || "",
    lot_size: prefill?.lot_size || "",
    notes: "",
  });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.symbol.trim()) { toast.error("Symbol required"); return; }
    setBusy(true);
    try {
      const payload = { ...form,
        entry_price: form.entry_price ? parseFloat(form.entry_price) : undefined,
        sl_price: form.sl_price ? parseFloat(form.sl_price) : undefined,
        tp_price: form.tp_price ? parseFloat(form.tp_price) : undefined,
        lot_size: form.lot_size ? parseFloat(form.lot_size) : undefined,
      };
      const { data } = await api.post("/companion/consult-trade", payload);
      setResult(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Forge is offline"); }
    finally { setBusy(false); }
  };

  const verdictColor = {
    GO: "text-[var(--profit)]",
    WAIT: "text-[var(--warning)]",
    SKIP: "text-[var(--loss)]",
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="consult-modal" onClick={onClose}>
      <div className="card-sharp p-6 max-w-xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp size={18} className="text-[var(--accent)]"/>
          <h3 className="font-display text-xl text-white flex-1">Should I take this trade?</h3>
          <button data-testid="consult-close" onClick={onClose}
            className="p-1.5 border border-white/10 rounded hover:border-[var(--loss)] hover:text-[var(--loss)]">
            <X size={13}/>
          </button>
        </div>

        {!result && (
          <>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <F label="Symbol">
                <input data-testid="consult-symbol" className="input-dark"
                  value={form.symbol} onChange={(e)=>setForm({...form, symbol: e.target.value.toUpperCase()})}/>
              </F>
              <F label="Side">
                <select data-testid="consult-side" className="input-dark"
                  value={form.side} onChange={(e)=>setForm({...form, side: e.target.value})}>
                  <option value="buy">BUY</option>
                  <option value="sell">SELL</option>
                </select>
              </F>
              <F label="Entry price"><input data-testid="consult-entry" type="number" step="0.00001" className="input-dark"
                value={form.entry_price} onChange={(e)=>setForm({...form, entry_price: e.target.value})}/></F>
              <F label="Lot size"><input data-testid="consult-lot" type="number" step="0.01" className="input-dark"
                value={form.lot_size} onChange={(e)=>setForm({...form, lot_size: e.target.value})}/></F>
              <F label="Stop Loss"><input data-testid="consult-sl" type="number" step="0.00001" className="input-dark"
                value={form.sl_price} onChange={(e)=>setForm({...form, sl_price: e.target.value})}/></F>
              <F label="Take Profit"><input data-testid="consult-tp" type="number" step="0.00001" className="input-dark"
                value={form.tp_price} onChange={(e)=>setForm({...form, tp_price: e.target.value})}/></F>
            </div>
            <F label="Notes (why do you want to take it?)">
              <textarea data-testid="consult-notes" className="input-dark !font-sans" rows={3}
                value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})}
                placeholder="e.g. RSI oversold on H1 with bullish divergence"/>
            </F>
            <button data-testid="consult-submit" onClick={submit} disabled={busy}
              className="btn-primary w-full mt-4 flex items-center justify-center gap-2">
              <Sparkles size={13}/> {busy ? "Forge is thinking…" : "Ask Forge"}
            </button>
          </>
        )}

        {result && (
          <div data-testid="consult-result">
            <div className={`text-4xl font-display font-bold ${verdictColor[result.verdict] || ""}`}
              data-testid="consult-verdict">
              {result.verdict}
            </div>
            <p className="text-[10px] font-mono text-[var(--text-muted)] uppercase tracking-widest mb-3">
              Forge's verdict — not financial advice
            </p>
            <div className="border border-white/10 rounded p-4 bg-black/30 whitespace-pre-wrap text-sm text-[var(--text-primary)]"
              data-testid="consult-reasoning">
              {result.reply}
            </div>
            <div className="flex gap-2 mt-4">
              <button data-testid="consult-again" onClick={()=>setResult(null)}
                className="btn-outline flex-1">Ask about another trade</button>
              <button data-testid="consult-done" onClick={onClose}
                className="btn-primary flex-1">Got it</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function F({ label, children }) {
  return <div><label className="label-tiny">{label}</label>{children}</div>;
}
